import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Play, Pause, RefreshCw, Voicemail, AlertCircle, FileText } from 'lucide-react';
import { fetchVoicemails, fetchVoicemailAudio } from './softphoneApi';

const formatDuration = (totalSeconds) => {
  const mins = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
  const secs = String(totalSeconds % 60).padStart(2, '0');
  return `${mins}:${secs}`;
};

const formatCaller = (raw) => {
  if (!raw) return 'Unknown caller';
  const digits = String(raw).replace(/[^\d+]/g, '');
  const match = digits.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
  return digits || 'Unknown caller';
};

const formatWhen = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const VoicemailList = ({ refreshSignal }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playingSid, setPlayingSid] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const audioRef = useRef(null);
  const urlCacheRef = useRef(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchVoicemails());
      setError('');
    } catch (loadError) {
      setError(loadError?.response?.data?.error || loadError.message || 'Could not load voicemails');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  useEffect(() => {
    const cache = urlCacheRef.current;
    return () => {
      if (audioRef.current) audioRef.current.pause();
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  const togglePlay = async (sid) => {
    if (playingSid === sid) {
      audioRef.current?.pause();
      setPlayingSid(null);
      return;
    }

    try {
      audioRef.current?.pause();
      let url = urlCacheRef.current.get(sid);
      if (!url) {
        url = await fetchVoicemailAudio(sid);
        urlCacheRef.current.set(sid, url);
      }
      const audio = new Audio(url);
      audio.onended = () => setPlayingSid(null);
      audio.onerror = () => {
        setError('Could not play that recording');
        setPlayingSid(null);
      };
      audioRef.current = audio;
      await audio.play();
      setPlayingSid(sid);
    } catch (playError) {
      setError(playError?.message || 'Could not play that recording');
      setPlayingSid(null);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <div className="text-sm font-semibold">Voicemails</div>
          <div className="mt-0.5 text-xs text-slate-500">
            {loading ? 'Loading…' : `${items.length} message${items.length === 1 ? '' : 's'}`}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="rounded-lg border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error ? (
        <div className="flex shrink-0 items-start gap-2 border-b border-rose-100 bg-rose-50 px-5 py-2.5 text-xs text-rose-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {!loading && items.length === 0 && !error ? (
          <div className="flex flex-col items-center px-3 py-10 text-center">
            <Voicemail className="h-8 w-8 text-slate-300" />
            <div className="mt-3 text-sm text-slate-400">No voicemails yet</div>
          </div>
        ) : (
          items.map((item) => (
            <div key={item.sid} className="rounded-xl px-3 py-2.5 hover:bg-slate-50">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => togglePlay(item.sid)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-600 text-white transition hover:bg-cyan-700"
                  title={playingSid === item.sid ? 'Pause' : 'Play'}
                >
                  {playingSid === item.sid ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{formatCaller(item.from)}</div>
                  <div className="text-xs text-slate-500">
                    {formatWhen(item.createdAt)} · {formatDuration(item.duration)}
                  </div>
                </div>
                {item.transcript ? (
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === item.sid ? null : item.sid)}
                    className={`shrink-0 rounded-lg border p-1.5 transition ${
                      expanded === item.sid
                        ? 'border-slate-300 bg-slate-100 text-slate-700'
                        : 'border-slate-200 text-slate-400 hover:bg-slate-50'
                    }`}
                    title="Transcript"
                  >
                    <FileText className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
              {expanded === item.sid && item.transcript ? (
                <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
                  {item.transcript}
                </p>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VoicemailList;
